/* eslint-disable prettier/prettier */
import * as React from "react";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Grid,
  Modal as MuiModal,
  TextField,
} from "@mui/material";
import { ClearIcon } from "@mui/x-date-pickers/icons";

// importing app setting
import { AuthenticationContext } from "../../../Context";
import { getHeaders } from "../../../utils/HeaderHelper";
import { useFlash } from "../../../FlashContext";

// import helpers
import { normalizeWhitespaceForSave } from "../../../utils/WhitespaceHelper";

interface Props {
  patient_id: number;
  edit_medication: any;
  open: boolean;
  setOpen: any;

  onLocalSave?: (medication: {
    patient_id: number;
    medication_product: string;
    value: string;
    directions: string;
    indication: string;
    prescriber: string;
    source: string;
  }) => void;
}

// constants
const DIRECTIONS_MAX_LENGTH = 500;
const INDICATION_MAX_LENGTH = 200;
const PRESCRIBER_MAX_LENGTH = 200;
const MODAL_WIDTH = 460;

const deriveDirections = (medication: any): string => {
  if (!medication) return "";
  if (medication.directions != null && medication.directions !== "") {
    return medication.directions;
  }
  return (medication.value || "").toString();
};

const FIELD_LABEL_SX = {
  fontFamily: "'QuicksandLight', 'Roboto', 'Helvetica', 'Arial', sans-serif",
  fontSize: "15px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "18px",
  color: "#4A4442",
  marginBottom: "6px",
};

const HELPER_TEXT_SX: React.CSSProperties = {
  fontFamily: "'QuicksandLight', 'Roboto', 'Helvetica', 'Arial', sans-serif",
  fontSize: "11px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "18px",
  color: "#4A4442",
  marginLeft: "12px",
};

const FIELD_SX = {
  width: "100% !important",
  backgroundColor: "#EFE9E7 !important",
  borderRadius: "5px",
  "& fieldset": {
    border: "none",
    outline: "none",
  },
  "& input::placeholder": {
    color: "rgba(74, 68, 66, 0.60)",
    opacity: 1,
    fontSize: "16px",
    fontWeight: 500,
    lineHeight: "22px",
  },
  "& input": {
    fontSize: "16px",
    fontWeight: 500,
    lineHeight: "22px",
    color: "#1E1E1E",
  },
} as const;



const AddMedication: React.FC<Props> = (props: any) => {
  const authenticationSetting = React.useContext(AuthenticationContext);
  const { showToast } = useFlash();

  const isEdit = props.edit_medication != null;
  const [name, setName] = React.useState<string>(
    isEdit
      ? (props.edit_medication.medication_product ||
          props.edit_medication.name ||
          "")
      : ""
  );
  const [directions, setDirections] = React.useState<string>(
    isEdit ? deriveDirections(props.edit_medication) : ""
  );
  const [indication, setIndication] = React.useState<string>(
    isEdit ? (props.edit_medication.indication || "") : ""
  );
  const [prescriber, setPrescriber] = React.useState<string>(
    isEdit ? (props.edit_medication.prescriber || "") : ""
  );

  const [medicationSearch, setMedicationSearch] = React.useState<
    string[] | null
  >(null);
  const [loading, setLoading] = React.useState<boolean>(false);

  const userEditedNameRef = React.useRef<boolean>(false);


  React.useEffect(() => {
    if (!name || !userEditedNameRef.current) return;

    let aborted = false;
    fetch(`/medication_search`, {
      method: "POST",
      headers: getHeaders(authenticationSetting.csrfToken),
      body: JSON.stringify({ medication: { name } }),
    })
      .then((result) => result.json())
      .then((result) => {
        if (aborted) return;
        if (typeof result.error !== "undefined") {
          setMedicationSearch(null);
          return;
        }
        setMedicationSearch(result?.data?.results || null);
      })
      .catch(() => {
        if (!aborted) setMedicationSearch(null);
      });

    return () => {
      aborted = true;
    };
  }, [name, authenticationSetting.csrfToken]);

  const handleClose = () => {
    if (loading) return;
    props.setOpen(false);
  };

  const handleSubmit = () => {
    if (!name?.trim()) {
      showToast({
        title: "Medication name required",
        text: "Please enter a medication name.",
        type: "error",
      });
      return;
    }

    setLoading(true);
    const normalizedDirections = normalizeWhitespaceForSave(directions);

    const normalizedMedication = {
      patient_id: props.patient_id,
      medication_product: normalizeWhitespaceForSave(name),
      value: normalizedDirections,
      directions: normalizedDirections,
      indication: normalizeWhitespaceForSave(indication),
      prescriber: normalizeWhitespaceForSave(prescriber),
      id: props.edit_medication?.id ?? null,
    };

    // Reconciliation mode: defer persistence to the parent component.
    if (!isEdit && typeof props.onLocalSave === "function") {
      props.onLocalSave({
        patient_id: props.patient_id,
        medication_product: normalizedMedication.medication_product,
        value: normalizedMedication.value,
        directions: normalizedMedication.directions,
        indication: normalizedMedication.indication,
        prescriber: normalizedMedication.prescriber,
        source: "MANUAL",
      });
      setLoading(false);
      props.setOpen(false);
      return;
    }

    fetch(isEdit ? "/update_medication" : "/add_medication", {
      method: "POST",
      headers: getHeaders(authenticationSetting.csrfToken),
      body: JSON.stringify({ medication: normalizedMedication }),
    })
      .then((result) => result.json())
      .then((result) => {
        if (result.error) {
          showToast({
            title: isEdit
              ? "Unable to update medication"
              : "Unable to add medication",
            text: result.error,
            type: "error",
          });
          setLoading(false);
          return;
        }

        showToast({
          title: isEdit ? "Medication updated" : "Medication added",
          text: isEdit
            ? "The medication has been updated."
            : "The medication has been added.",
          type: "success",
        });
        setLoading(false);
        props.setOpen(false);
      })
      .catch((err) => {
        showToast({
          title: isEdit
            ? "Unable to update medication"
            : "Unable to add medication",
          text: err?.message || "Something went wrong. Please try again.",
          type: "error",
        });
        setLoading(false);
      });
  };

  const submitLabel = isEdit ? "Edit Medication" : "Add Medication";
  const modalTitle = isEdit ? "Edit Medication" : "Add Medication";

  return (
    <MuiModal
      open={props.open}
      className="modal-primary"
      onClose={handleClose}
    >
      <div className="paper" style={{ width: MODAL_WIDTH }}>
        <div className="paperInner">
          <Grid container>
            <Grid item xs={12}>
              <div className="main-header">
                <span>{modalTitle}</span>
                <span id="dismiss-button" onClick={handleClose}>
                  <ClearIcon sx={{ width: 30, height: 30 }} />
                </span>
              </div>
            </Grid>

            <Grid
              item
              xs={12}
              sx={{
                paddingLeft: "20px",
                paddingRight: "20px",
                paddingTop: "8px",
                paddingBottom: "12px",
                display: "flex",
                flexDirection: "column",
                rowGap: "20px",
              }}
            >
              {/* Medication Name */}
              <Grid container>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      flexWrap: "wrap",
                      marginBottom: "6px",
                      columnGap: "8px",
                    }}
                  >
                    <Box
                      component="label"
                      htmlFor="medication_name"
                      sx={{ ...FIELD_LABEL_SX, marginBottom: 0 }}
                    >
                      Medication Name*
                    </Box>
                    <span style={{ ...HELPER_TEXT_SX, marginLeft: 0, whiteSpace: "nowrap" }}>
                      Begin typing a name to search for existing medications.
                    </span>
                  </Box>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="medication_name"
                    size="small"
                    value={name}
                    variant="outlined"
                    placeholder="Medication Name"
                    disabled={loading}
                    required
                    onChange={(event) => {
                      userEditedNameRef.current = true;
                      setName(event.target.value);
                    }}
                    sx={FIELD_SX}
                  />

                  {medicationSearch && medicationSearch.length > 0 && (
                    <div
                      style={{
                        marginTop: 6,
                        border: "1px solid #929292",
                        borderRadius: 6,
                        maxHeight: 160,
                        overflow: "auto",
                        padding: 8,
                        backgroundColor: "#fff",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          color: "#4A4442",
                          marginBottom: 4,
                        }}
                      >
                        Available Medications{" "}
                        <span style={{ fontWeight: 400 }}>
                          (Click medication below to populate field)
                        </span>
                      </div>
                      {medicationSearch.map((medication, index) => (
                        <div
                          key={`${medication}-${index}`}
                          className={index % 2 === 0 ? "rowEven" : "row"}
                          style={{
                            cursor: "pointer",
                            padding: 6,
                            fontFamily: "QuicksandMedium",
                          }}
                          onClick={() => {
                            setName(medication);
                            setMedicationSearch(null);
                          }}
                        >
                          {medication}
                        </div>
                      ))}
                    </div>
                  )}
                </Grid>
              </Grid>

              {/* Directions */}
              <Grid container>
                <Grid item xs={12}>
                  <Box
                    component="label"
                    htmlFor="medication_directions"
                    sx={{ ...FIELD_LABEL_SX, display: "block" }}
                  >
                    Directions
                  </Box>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="medication_directions"
                    size="small"
                    value={directions}
                    variant="outlined"
                    placeholder="Directions"
                    disabled={loading}
                    onChange={(event) => {
                      const next = event.target.value;
                      if (next.length > DIRECTIONS_MAX_LENGTH) return;
                      setDirections(next);
                    }}
                    inputProps={{ maxLength: DIRECTIONS_MAX_LENGTH }}
                    sx={FIELD_SX}
                  />
                </Grid>
              </Grid>

              {/* Indication */}
              <Grid container>
                <Grid item xs={12}>
                  <Box
                    component="label"
                    htmlFor="medication_indication"
                    sx={{ ...FIELD_LABEL_SX, display: "block" }}
                  >
                    Indication
                  </Box>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="medication_indication"
                    size="small"
                    value={indication}
                    variant="outlined"
                    placeholder="Indication"
                    disabled={loading}
                    onChange={(event) => {
                      const next = event.target.value;
                      if (next.length > INDICATION_MAX_LENGTH) return;
                      setIndication(next);
                    }}
                    inputProps={{ maxLength: INDICATION_MAX_LENGTH }}
                    sx={FIELD_SX}
                  />
                </Grid>
              </Grid>

              {/* Prescriber */}
              <Grid container>
                <Grid item xs={12}>
                  <Box
                    component="label"
                    htmlFor="medication_prescriber"
                    sx={{ ...FIELD_LABEL_SX, display: "block" }}
                  >
                    Prescriber
                  </Box>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="medication_prescriber"
                    size="small"
                    value={prescriber}
                    variant="outlined"
                    placeholder="Prescriber"
                    disabled={loading}
                    onChange={(event) => {
                      const next = event.target.value;
                      if (next.length > PRESCRIBER_MAX_LENGTH) return;
                      setPrescriber(next);
                    }}
                    inputProps={{ maxLength: PRESCRIBER_MAX_LENGTH }}
                    sx={FIELD_SX}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            className="modal-footer"
          >
            <Grid item xs={12} className="confirm-btn-container">
              <div onClick={handleClose} className="cancel-link">
                Cancel
              </div>
              <Button
                onClick={handleSubmit}
                className="confirm-btn"
                style={{ width: 180 }}
                disabled={loading}
              >
                {submitLabel}
              </Button>
            </Grid>
          </Grid>
        </div>
        <Backdrop open={loading} style={{ zIndex: 9999, color: "#fff" }}>
          <CircularProgress />
        </Backdrop>
      </div>
    </MuiModal>
  );
};

export default AddMedication;
